// contactToRelationItems.ts — bestehende Beziehungen eines Kontakts in die
// Eingabe-Formate der Anlage-Sektionen übersetzen, damit die Umwandlung
// (Firma ↔ Person) sie vorausgefüllt und editierbar zeigen kann.
//
// Gegenstück zu persistRelations.ts (Items → API). Die Rückabbildung
// „beide Richtungen normalisieren" gibt es bereits in ContactConnections.mkRel;
// die Rollen-Rückabbildung in relsToVerant. Hier stehen sie in der Form, die
// die Listen-Komponenten erwarten.
//
// Zieltyp-abhängig, weil die Sektionen unterschiedlich sind:
//   → Person : rel_household (Haushalt), rel_weitere, rel_personFirma
//   → Firma  : rel_menschen, rel_firmaConn
//
// Seit 27.09.2026 auch für den Bearbeiten-Modus der Akte (`opts.bearbeiten`,
// Zieltyp = eigener Typ). Dort kommen zusätzlich Vertretungen, Vereine und der
// Arbeitgeber zurück, eingehende Verwandtschaften zeigen die Gegenrichtung, und
// Beziehungen, die die Maske nicht führt, bleiben draußen (`unberuehrt`) —
// sie dürfen beim Speichern weder geändert noch gelöscht werden.
//
// Herkunft-Verknüpfungen (Empfehlungsgeber, Tippgeber/Vermittler und ihre
// Gegenrichtungen, T-203) sind keine Beziehung im Sinne der Maske: Sie stehen im
// Abschnitt Herkunft und unter „Verknüpfungen", aber nicht in den Beziehungs-
// karten, im Netzwerk oder im Organigramm. Im Bearbeiten-Modus landen sie in
// `unberuehrt`, bei der Umwandlung werden sie nicht übernommen (die Zeile bleibt
// serverseitig bestehen).
//
// Beziehungen, deren Rolle beim Umwandeln ihren Sinn verliert (eine Person führt
// keine „Menschen fürs Unternehmen"), kommen bewusst OHNE Rolle zurück — genau
// die soll der Anwender in der Maske neu vergeben.

import type {
  ContactRef,
  FirmaConnItem,
  HouseholdItem,
  PersonFirmaItem,
  AssistantItem,
  VertretungItem,
} from "../components/relationsInline/types";
import type { VereinEintrag } from "../components/forms/widgets/VereinMitgliedschaften";
import { istVertretungsrolle } from "../data/vertretungsrollen";
import { istHerkunftBeziehung } from "../data/herkunftBeziehungstypen";
import { istVereinsRechtsform } from "../data/vereinsansicht";
import { reverseKinshipLabel } from "../services/haushalteCleanupService";
import { genderOf } from "./householdRole";
import { uid } from "../components/relationsInline/types";
import {
  BETEILIGUNG,
  firmaConnRolleAusRichtung,
  FIRMA_STRUKTUR_TYPEN_SET,
  GESELLSCHAFTERIN,
} from "../data/firmenverbindungen";
import { anteilsFakt, faktSchluessel, fasseAnteilsZeilenZusammen } from "../data/anteilsbeziehung";
import { ASSISTENZ_ROLLE } from "../data/privateAssistenz";
import type { MenschEntry } from "../components/relationsInline/MenschenFuersUnternehmenList";
import type { VerantwortlichkeitValue, VerantRole } from "../components/verantwortlichkeit/VerantwortlichkeitInput";

type AnyRec = Record<string, any>;

/** Rollen-Typen, die eine Zuständigkeit IM Unternehmen beschreiben. */
const ROLE_TYPES = [
  "Mensch fürs Unternehmen",
  "Mensch im Unternehmen",
  "Geschäftsführer",
  "Gesellschafter",
  "Mitarbeiter",
];

const s = (v: unknown): string => (v == null ? "" : String(v));

const nameOf = (p: AnyRec): string => {
  const composed = `${s(p.lastname)}${p.firstName ? ", " + s(p.firstName) : ""}`
    .replace(/^,\s*|,\s*$/g, "")
    .trim();
  return s(p.displayName) || composed || "—";
};

const isFirma = (p: AnyRec): boolean => (p?.type?.type ?? p?.typ ?? "") === "Firma";

/**
 * Gegenkontakt → ContactRef (bestehender Kontakt, also mit contactId).
 *
 * `firmaId`: Die Beziehung steht in der Akte DIESER Firma (Zieltyp Firma) —
 * dann zählt als Telefon/E-Mail nur die geschäftliche Kommunikation der Person
 * zu genau dieser Firma (User 30.09.2026, siehe `kommunikationVon`).
 */
const toRef = (other: AnyRec, firmaId?: number): ContactRef => ({
  contactId: other?.id,
  type: isFirma(other) ? "Firma" : "Person",
  firstName: s(other?.firstName) || undefined,
  lastName: s(other?.lastname) || undefined,
  gender: s(other?.gender) || undefined,
  displayName: nameOf(other ?? {}),
  // Rechtsform der Firma (eager geladen): ohne sie meldet die Verantwortlichkeit
  // einer Person fälschlich „Ohne Rechtsform der Firma …" (User 30.09.2026).
  ...(isFirma(other)
    ? {
        legalForm: s(other?.legalForm?.legalForm) || undefined,
        legalFormId: Number(other?.legalForm?.id) || undefined,
      }
    : {}),
  // Für das Kreuz im Organigramm der Anlage (User 28.09.2026).
  ...(other?.deceased === true || !!other?.deceasedAt ? { deceased: true } : {}),
  // Nur Anzeige in den Beziehungslisten (Spalten wie in der Akte).
  ...(s(other?.status?.status) ? { status: s(other.status.status) } : {}),
  ...kommunikationVon(other, firmaId),
});

/**
 * Telefonnummer und E-Mail eines Gegenkontakts — nur, wenn die
 * Kommunikationswege mitgeladen sind (`relations-deep`). Bei einer Firma nie:
 * Ihr Popup zeigt auch die Nummern ihrer Menschen, ein „hat keine" wäre falsch.
 *
 * Ohne `firmaId` (Personen-Sicht): die erste Nummer bzw. Adresse, egal welcher Art.
 *
 * Mit `firmaId` (Akte einer Firma, User 30.09.2026): NUR die geschäftliche
 * Kommunikation mit Firmenbezug zu dieser Firma — Telefon ist `type=phone`
 * (kein Handy), E-Mail `type=email`, jeweils `kind=geschaeftlich` und
 * `firmaId` unter ihren Unternehmen (`companyIds`). Bis dahin stand in der Zeile die erste beliebige
 * Nummer, auch das private Handy. Die ids gehen mit, damit das
 * Sofort-Speichern genau diese Zeile ändert (und nie eine fremde).
 * Hat die Person nur ANDERE Wege, bleibt `telefon`/`email` `undefined`
 * („unbekannt"): Das Symbol der Kontaktmöglichkeit erscheint dann weiter,
 * nennt aber keine private Nummer.
 */
function kommunikationVon(other: AnyRec, firmaId?: number): Pick<ContactRef, "kommunikation"> {
  if (isFirma(other) || !Array.isArray(other?.communications)) return {};
  const alle = (other.communications as AnyRec[]).filter((c) => s(c?.value).trim());
  const erste = (typen: string[]) => alle.find((c) => typen.includes(s(c?.type).toLowerCase()));
  const wert = (c: AnyRec | undefined) => s(c?.value).trim() || null;
  if (firmaId == null) {
    return {
      kommunikation: {
        telefon: wert(erste(["phone", "mobile", "telefon", "mobil"])),
        email: wert(erste(["email", "e-mail"])),
      },
    };
  }
  // Seit 02.10.2026 kann ein Weg mehreren Unternehmen gehören: Er erscheint
  // bei JEDEM zugeordneten (`companyIds` der API; ältere Antworten: companyId).
  const gehoertFirma = (c: AnyRec) => {
    const ids: unknown[] = Array.isArray(c?.companyIds) ? c.companyIds : c?.companyId != null ? [c.companyId] : [];
    return ids.some((id) => Number(id) === Number(firmaId));
  };
  const geschaeftlich = (typ: string) =>
    alle.find(
      (c) =>
        s(c?.type).toLowerCase() === typ &&
        s(c?.kind) === "geschaeftlich" &&
        gehoertFirma(c),
    );
  const tel = geschaeftlich("phone");
  const mail = geschaeftlich("email");
  // Symbol-Logik (BeziehungsTabelle): `null` = hat keine, `undefined` = unbekannt.
  const sonst = (c: AnyRec | undefined, typen: string[]) => (c ? wert(c) : erste(typen) ? undefined : null);
  return {
    kommunikation: {
      telefon: sonst(tel, ["phone", "mobile", "telefon", "mobil"]),
      email: sonst(mail, ["email", "e-mail"]),
      telefonId: tel ? Number(tel.id) || null : null,
      emailId: mail ? Number(mail.id) || null : null,
      firmaId,
    },
  };
}

/** Eine Beziehung aus Sicht des Kontakts, Richtung schon aufgelöst. */
interface Flat {
  relationId?: number;
  srcId?: number;
  destId?: number;
  other: AnyRec;
  relType: string;
  func: string;
  share: string;
  isExtern: boolean;
  sameHousehold: boolean;
  reverse: string;
  /** Markierter Hauptarbeitgeber (Person → Firma). */
  istHauptarbeitgeber: boolean;
  /** PO-Haken „Mensch im Unternehmen" an der Verbindung. */
  istMenschImUnternehmen: boolean;
  istMenschImUnternehmenManuell: boolean;
  /** „Person ist ausgeschieden?" mit Beginn/Ende (YYYY-MM-DD, sonst ""). */
  ausgeschieden: boolean;
  beginn: string;
  ende: string;
}

/**
 * `relationsSrc` + `relationsDest` zu einer Liste vereinen. Anders als in der
 * Anzeige wird das Label NICHT invertiert: hier zählt der gespeicherte Wert,
 * denn er wird beim Speichern wieder so geschrieben.
 */
export function flattenRelations(contact: AnyRec): Flat[] {
  const selfId: number | undefined = contact?.id;
  const src: AnyRec[] = contact?.relationsSrc ?? contact?.relations ?? [];
  const dest: AnyRec[] = contact?.relationsDest ?? [];
  const out: Flat[] = [];

  const push = (r: AnyRec, other: AnyRec, inverse: boolean) => {
    if (!other) return;
    out.push({
      relationId: r.id,
      srcId: inverse ? other.id : selfId,
      destId: inverse ? selfId : other.id,
      other,
      relType: s(r.relationType?.contactRelationType),
      func: s(r.function),
      share: s(r.share),
      isExtern: !!r.isExtern,
      sameHousehold: !!r.sameHousehold,
      reverse: s(r.reverseRelationType),
      istHauptarbeitgeber: !!r.istHauptarbeitgeber,
      istMenschImUnternehmen: !!r.istMenschImUnternehmen,
      istMenschImUnternehmenManuell: !!r.istMenschImUnternehmenManuell,
      ausgeschieden: !!r.ausgeschieden,
      beginn: s(r.beginn).slice(0, 10),
      ende: s(r.ende).slice(0, 10),
    });
  };

  for (const r of src) push(r, r.contactDest, false);
  for (const r of dest) push(r, r.contactSrc, true);
  return out.filter((f) => f.other?.id && f.other.id !== selfId && !f.other.deleted);
}

/** Rollen einer Beziehung → VerantwortlichkeitValue (Prefill der Rollen-Eingabe). */
const toVerant = (rows: Flat[]): VerantwortlichkeitValue => {
  const roles: VerantRole[] = [];
  let isExtern = false;
  for (const r of rows) {
    if (r.relType === "Geschäftsführer") {
      roles.push({ kind: "gf" });
    } else if (r.relType === "Gesellschafter") {
      roles.push({
        kind: "gesellschafter",
        share: r.share,
        istGeschaeftsfuehrer: r.func === "geschäftsführend",
        // Nur zum Erkennen der Komplementär-GmbH (T11) — geschrieben wird sie nicht.
        ...(r.func.trim() && r.func !== "geschäftsführend" ? { funktion: r.func.trim() } : {}),
      });
    } else if (r.relType === "Mensch im Unternehmen" && !r.func.trim()) {
      // Eigene Zeilenart ohne Funktion: Sie IST der Haken „Mensch im
      // Unternehmen" (s. u.), keine Verantwortlichkeit. Als Rolle stünde die
      // Person sonst mit „—" unter „Verantwortung fürs Unternehmen".
      continue;
    } else if (ROLE_TYPES.includes(r.relType)) {
      roles.push({ kind: "mensch", verantwortlichkeit: r.func, share: r.share || undefined });
      if (r.isExtern) isExtern = true;
    }
  }
  // Der PO-Haken „Mensch im Unternehmen" hängt an der Verbindung — ohne ihn
  // wüsste die Liste „Mensch im Unternehmen" der Firmenkarte nichts vom Bestand.
  const eigeneZeile = rows.some((r) => r.relType === "Mensch im Unternehmen");
  const haken = eigeneZeile || rows.some((r) => r.istMenschImUnternehmen);
  const manuell = eigeneZeile || rows.some((r) => r.istMenschImUnternehmenManuell);
  return {
    isExtern,
    roles,
    externalCompany: null,
    ...(haken ? { istMenschImUnternehmen: true } : {}),
    ...(manuell ? { istMenschImUnternehmenManuell: true } : {}),
  };
};

/** Mehrere Beziehungen zum selben Gegenkontakt bündeln (Rollen gehören zusammen). */
const groupByOther = (rows: Flat[]): Map<number, Flat[]> => {
  const m = new Map<number, Flat[]>();
  for (const r of rows) {
    const key = r.other.id as number;
    m.set(key, [...(m.get(key) ?? []), r]);
  }
  return m;
};

/**
 * „Ausgeschieden" einer gebündelten Verbindung: Das Feld gilt für das Paar,
 * gelesen wird deshalb die erste Zeile, die es trägt.
 */
const ausgeschiedenVon = (rows: Flat[]): { ausgeschieden: boolean; beginn?: string; ende?: string } => {
  const r = rows.find((x) => x.ausgeschieden);
  // Eintrittsdatum auch ohne Ausscheiden (User 28.09.2026).
  if (!r) {
    const mitBeginn = rows.find((x) => x.beginn);
    return { ausgeschieden: false, ...(mitBeginn ? { beginn: mitBeginn.beginn } : {}) };
  }
  return {
    ausgeschieden: true,
    ...(r.beginn ? { beginn: r.beginn } : {}),
    ...(r.ende ? { ende: r.ende } : {}),
  };
};

/** Alle Beziehungs-Ids einer Gruppe — der Abgleich braucht sie vollständig. */
const idsOf = (rows: Flat[]): number[] =>
  rows.map((r) => r.relationId).filter((n): n is number => typeof n === "number");

/**
 * Gespeicherter Typ → Rolle AUS SICHT des Kontakts.
 *
 * Zwei gerichtete Typen, beide kanonisch als EINE Zeile gehalten:
 *   'Tochterfirma': src = Eltern,       dest = Kind          → auf der dest-Seite „Mutterfirma".
 *   'Beteiligung':  src = Gesellschaft, dest = Gesellschafter → auf der src-Seite „Gesellschafterin".
 *
 * Unbesehen übernommen stünde in der Maske die Gegenbedeutung, und das
 * Speichern über canonicalFirmaConn drehte die Kante um.
 */
const firmaConnRole = (f: Flat, selfId: number | undefined): string => {
  // Geprüft wird gegen die GESPEICHERTEN Typen, nicht gegen die Auswahlliste:
  // „Mutterfirma" und „Gesellschafterin" stehen dort als Rolle, kommen aber nie
  // aus der Datenbank. Alles andere (etwa eine Zuständigkeit, deren Rolle beim
  // Umwandeln ihren Sinn verliert) fällt bewusst auf „Beteiligung" — die
  // schwächste Aussage, die der Anwender in der Maske überschreiben kann.
  //
  // Ausnahme 'Gesellschafter' zwischen zwei Firmen: Die Richtung ist bekannt
  // (src = Gesellschaft, dest = Gesellschafter). Ausgehend hält die ANDERE
  // Firma Anteile an diesem Kontakt — das ist „Gesellschafterin", nicht
  // „Beteiligung". Bis 28.09.2026 stand hier pauschal „Beteiligung", also das
  // Gegenteil des Sachverhalts; ein Speichern hätte die Kante umgedreht.
  if (f.relType === "Gesellschafter") return f.srcId === selfId ? GESELLSCHAFTERIN : BETEILIGUNG;
  if (!FIRMA_STRUKTUR_TYPEN_SET.has(f.relType)) return BETEILIGUNG;
  return firmaConnRolleAusRichtung(f.relType, f.srcId === selfId);
};

/**
 * Beziehungen zu FIRMEN in der Firmenmaske (Bearbeiten-Modus) aufteilen
 * (User 28.09.2026):
 *
 *  • Dieselbe Beteiligung steht oft zweimal in den Daten — 'Tochterfirma' der
 *    Mutter und 'Gesellschafter' der Tochter. Sie wird EIN Eintrag in
 *    „Verbindungen zu Firmen" (Leitzeile = Struktur-Zeile, die andere als
 *    `spiegel`), Quote aus der Zeile, die eine hat.
 *  • Eine Firma, die NUR als Gesellschafterin (oder Geschäftsführerin,
 *    Verantwortliche) an diesem Kontakt eingetragen ist, gehört zu den
 *    Rollen der Karte „Verantwortung fürs Unternehmen" — dort, wo auch die
 *    Anlage sie erfasst (attach-menschen, src = dieses Unternehmen).
 *
 * Liefert die Firmen-Einträge und die Zeilen, die als Rollen weitergehen.
 */
function firmenZeilenAufteilen(
  rows: Flat[],
  selfId: number | undefined,
): { firmaConn: FirmaConnItem[]; alsRollen: Flat[] } {
  const alsRollen: Flat[] = [];
  const firmaConn: FirmaConnItem[] = [];

  // Gruppen derselben Beteiligung: Enthält eine Gruppe nur ausgehende
  // Gesellschafter-Zeilen, ist sie eine ROLLE der anderen Firma an uns.
  const gruppe = new Map<string, Flat[]>();
  for (const f of rows) {
    const fakt = anteilsFakt(f.relType, f.srcId, f.destId);
    if (fakt) gruppe.set(faktSchluessel(fakt), [...(gruppe.get(faktSchluessel(fakt)) ?? []), f]);
  }
  const nurRolle = (f: Flat): boolean => {
    if (f.srcId !== selfId || !ROLE_TYPES.includes(f.relType)) return false;
    const fakt = anteilsFakt(f.relType, f.srcId, f.destId);
    if (!fakt) return true; // Geschäftsführer, Mensch fürs Unternehmen …
    return (gruppe.get(faktSchluessel(fakt)) ?? []).every(
      (g) => g.relType === "Gesellschafter" && g.srcId === selfId,
    );
  };

  const verbindungen: Flat[] = [];
  for (const f of rows) (nurRolle(f) ? alsRollen : verbindungen).push(f);

  for (const g of fasseAnteilsZeilenZusammen(verbindungen, (f) => ({
    typ: f.relType,
    srcId: f.srcId,
    destId: f.destId,
    share: f.share,
  }))) {
    const f = g.zeile;
    firmaConn.push({
      id: uid(),
      ref: toRef(f.other),
      relationId: f.relationId,
      relationIds: idsOf([f]),
      srcId: f.srcId,
      destId: f.destId,
      role: firmaConnRole(f, selfId),
      share: g.share ?? (f.share || undefined),
      ...(g.spiegel.length
        ? {
            spiegel: g.spiegel
              .filter((sp) => typeof sp.relationId === "number")
              .map((sp) => ({ id: sp.relationId as number, typ: sp.relType, srcId: sp.srcId as number, destId: sp.destId as number })),
          }
        : {}),
      ...(g.anteilWiderspruch ? { anteilWiderspruch: g.anteilWiderspruch } : {}),
    });
  }
  return { firmaConn, alsRollen };
}

/** Eine Vertretung im Format der Personenmaske (`rel_vertretung`). */
export type VertretungPrefill = VertretungItem & { zielName?: string };

/** Ein Verein im Format der Mitgliedschaften-Karte, plus die Beziehungszeilen. */
export type VereinPrefill = VereinEintrag & {
  relationId?: number;
  relationIds?: number[];
  srcId?: number;
  destId?: number;
};

export interface RelationPrefill {
  rel_household: HouseholdItem[];
  rel_weitere: HouseholdItem[];
  rel_assistenz: AssistantItem[];
  rel_personFirma: PersonFirmaItem[];
  rel_menschen: MenschEntry[];
  rel_firmaConn: FirmaConnItem[];
  rel_kvGroupId: string;
  /** Nur Bearbeiten-Modus: „Dieser Kontakt vertritt …". */
  rel_vertretung: VertretungPrefill[];
  /** Nur Bearbeiten-Modus (Person und Firma): Mitgliedschaften in Vereinen. */
  vereine: VereinPrefill[];
  /** Markierter Hauptarbeitgeber (Person) — sonst null. */
  arbeitgeber: { id: number; name: string } | null;
  /**
   * Nur Bearbeiten-Modus: Beziehungs-Ids, die die Maske NICHT führt (etwa
   * „wird vertreten von", „ist Assistenz von"). Sie gehören nicht in
   * `rel_previousIds`, sonst löschte der Abgleich sie.
   */
  unberuehrt: number[];
}

/**
 * Beziehungen für die Umwandlung aufbereiten.
 *
 * `target` ist der ZIEL-Typ. Beziehungen zu Personen, deren Rolle im Ziel keinen
 * Sinn ergibt, kommen ohne Rolle zurück (leeres Label bzw. leere
 * Verantwortlichkeit) — der Anwender vergibt sie in der Maske neu.
 */
export function contactToRelationItems(
  contact: AnyRec,
  target: "Firma" | "Person",
  opts: { bearbeiten?: boolean } = {},
): RelationPrefill {
  const bearbeiten = !!opts.bearbeiten;
  const selfId: number | undefined = contact?.id;
  let flat = flattenRelations(contact);
  const kv = contact?.group?.type === "Kundenverbindung" ? s(contact?.group?.id) : "";

  const out: RelationPrefill = {
    rel_household: [],
    rel_weitere: [],
    rel_assistenz: [],
    rel_personFirma: [],
    rel_menschen: [],
    rel_firmaConn: [],
    rel_kvGroupId: kv,
    rel_vertretung: [],
    vereine: [],
    arbeitgeber: null,
    unberuehrt: [],
  };

  // Hauptarbeitgeber: die markierte Person→Firma-Beziehung (P12). Die Zeile
  // selbst bleibt unten in `rel_personFirma` — markiert wird sie über den
  // eigenen Endpunkt (`arbeitgeberSetzen`).
  const ag = flat.find((f) => f.istHauptarbeitgeber && isFirma(f.other));
  if (target === "Person" && ag) out.arbeitgeber = { id: ag.other.id, name: nameOf(ag.other) };

  // Herkunft (T-203): nie als Beziehung, Kante oder Beteiligung. Im Bearbeiten-
  // Modus unangetastet lassen (nicht in rel_previousIds → kein Löschen beim
  // Abgleich), bei der Umwandlung schlicht nicht übernehmen.
  flat = flat.filter((f) => {
    if (!istHerkunftBeziehung(f)) return true;
    if (bearbeiten && f.relationId != null) out.unberuehrt.push(f.relationId);
    return false;
  });

  if (bearbeiten) {
    const rest: Flat[] = [];
    // Vertretungen je Vertretenem bündeln: mehrere Rollen, EIN Eintrag.
    const vertretungen = new Map<number, Flat[]>();
    for (const f of flat) {
      if (istVertretungsrolle(f.relType)) {
        // Nur „dieser Kontakt vertritt …" (Vertreter = dest). Die Gegenrichtung
        // gehört zu den Vollmachten im Header.
        if (f.destId === selfId && target === "Person") {
          vertretungen.set(f.other.id, [...(vertretungen.get(f.other.id) ?? []), f]);
        } else if (f.relationId != null) {
          out.unberuehrt.push(f.relationId);
        }
        continue;
      }
      if (f.relType === ASSISTENZ_ROLLE && f.srcId !== selfId) {
        // Dieser Kontakt IST die Assistenz eines anderen — nicht Teil der Maske.
        if (f.relationId != null) out.unberuehrt.push(f.relationId);
        continue;
      }
      if (
        // Person UND Firma (02.10.2026): Auch eine Firma ist Mitglied in
        // Vereinen und Verbänden (Karte „Mitgliedschaften"). Bis dahin landete
        // die Zeile bei der Firma unter „Verbindungen zu Firmen" — als
        // „Beteiligung", dem Gegenteil des Sachverhalts.
        isFirma(f.other) &&
        f.relType === "Mensch fürs Unternehmen" &&
        f.destId === selfId &&
        istVereinsRechtsform(f.other?.legalForm?.legalForm ?? f.other?.legalForm)
      ) {
        // Mitgliedschaft im Verein (P4) — dieselbe Zeile, die die Anlage für die
        // Karte „Vereine und Mitgliedschaften" schreibt.
        const func = f.func.trim();
        out.vereine.push({
          id: `verein-${f.relationId ?? f.other.id}`,
          vereinName: nameOf(f.other),
          vereinContactId: f.other.id,
          hatMitgliedschaft: func !== "",
          funktion: func === "Mitglied" ? "" : func,
          ref: toRef(f.other),
          relationId: f.relationId,
          relationIds: idsOf([f]),
          srcId: f.srcId,
          destId: f.destId,
        });
        continue;
      }
      rest.push(f);
    }
    for (const [, rows] of vertretungen) {
      const other = rows[0].other;
      out.rel_vertretung.push({
        id: `vt-${rows[0].relationId ?? other.id}`,
        ref: toRef(other),
        rolle: rows[0].relType,
        rollen: rows.map((r) => r.relType),
        zielName: nameOf(other),
        relationId: rows[0].relationId,
        relationIds: idsOf(rows),
        srcId: rows[0].srcId,
        destId: rows[0].destId,
      });
    }
    flat = rest;
  }

  const toPersons = flat.filter((f) => !isFirma(f.other));
  const toCompanies = flat.filter((f) => isFirma(f.other));

  if (target === "Person") {
    // Beziehungen zu Personen: Haushalt bleibt Haushalt. Rollen-Typen ergeben
    // für eine Person keinen Sinn mehr → Label leer, damit die Beziehung in der
    // Maske bewusst benannt wird.
    for (const f of toPersons) {
      if (f.relType === ASSISTENZ_ROLLE) {
        const other = f.other;
        out.rel_assistenz.push({
          id: uid(),
          ref: toRef(other),
          contactId: other?.id,
          anrede: other?.salutation?.salutation || "",
          title: other?.title?.title || "",
          lastName: other?.lastname || "",
          firstName: other?.firstName || "",
          phone: other?.phone || "",
          mobile: other?.mobile || "",
          email: other?.email || "",
          // Wofür die Assistenz zuständig ist — sonst leerte der Abgleich sie.
          aufgabe: f.func,
          relationId: f.relationId,
          relationIds: idsOf([f]),
          srcId: f.srcId,
          destId: f.destId,
        });
        continue;
      }
      if (bearbeiten) {
        // Eigener Typ: Rollen verlieren nichts. Eine eingehende Zeile (dieser
        // Kontakt = dest) gilt aus Sicht des Gegenübers — für die Maske die
        // Gegenrichtung bilden, wie die Akte es tut (ContactConnections.mkRel).
        const eingehend = f.destId === selfId;
        const label = eingehend
          ? f.reverse || reverseKinshipLabel(f.relType, genderOf(f.other), { partnerFallback: "opposite" }) || f.relType
          : f.relType;
        const item: HouseholdItem = {
          id: uid(),
          ref: toRef(f.other),
          relationId: f.relationId,
          relationIds: idsOf([f]),
          srcId: f.srcId,
          destId: f.destId,
          relationLabel: label,
          sameHousehold: f.sameHousehold,
        };
        (f.sameHousehold ? out.rel_household : out.rel_weitere).push(item);
        continue;
      }
      const roleLost = ROLE_TYPES.includes(f.relType);
      const item: HouseholdItem = {
        id: uid(),
        ref: toRef(f.other),
        relationId: f.relationId,
        relationIds: idsOf([f]),
        srcId: f.srcId,
        destId: f.destId,
        relationLabel: roleLost ? "" : f.relType,
        sameHousehold: f.sameHousehold,
        freeRole: roleLost ? "" : undefined,
        freeRoleReverse: roleLost ? "" : f.reverse || undefined,
      };
      (f.sameHousehold ? out.rel_household : out.rel_weitere).push(item);
    }
    // Verbindungen zu Firmen bleiben inhaltlich gültig (die Person ist dort
    // Geschäftsführerin o. ä.) → mit ihren Rollen übernehmen, je Firma gebündelt.
    for (const [, rows] of groupByOther(toCompanies)) {
      out.rel_personFirma.push({
        id: uid(),
        ref: toRef(rows[0].other),
        relationId: rows[0].relationId,
        // Vollständig, nicht nur die erste: „Geschäftsführer und Gesellschafter"
        // derselben Firma sind zwei Zeilen hinter EINEM Kärtchen.
        relationIds: idsOf(rows),
        srcId: rows[0].srcId,
        destId: rows[0].destId,
        verant: toVerant(rows),
        ...ausgeschiedenVon(rows),
      });
    }
    return out;
  }

  // target === "Firma"
  // Im Bearbeiten-Modus: Firmen mit einer Rolle an diesem Unternehmen gehen zu
  // den Menschen fürs Unternehmen, Beteiligungen werden je Tatsache gebündelt.
  const firmen = bearbeiten ? firmenZeilenAufteilen(toCompanies, selfId) : null;
  const rollenTraeger = firmen ? [...toPersons, ...firmen.alsRollen] : toPersons;

  // Personen werden zu „Menschen fürs Unternehmen". Verwandtschaftsbeziehungen
  // verlieren dabei ihre Rolle → leere Verantwortlichkeit, in der Maske zu füllen.
  for (const [, rows] of groupByOther(rollenTraeger)) {
    const hasRole = rows.some((r) => ROLE_TYPES.includes(r.relType));
    out.rel_menschen.push({
      id: uid(),
      ref: toRef(rows[0].other, selfId),
      // Diese Zeilen ersetzt der attach-menschen-Endpoint transaktional; der
      // Abgleich muss sie in Ruhe lassen (sonst löscht er, was gerade neu
      // geschrieben wurde — oder schlimmer: was nach einem Fehlschlag noch steht).
      relationIds: idsOf(rows),
      verant: hasRole ? toVerant(rows) : { isExtern: false, roles: [], externalCompany: null },
      ...ausgeschiedenVon(rows),
    });
  }
  if (firmen) {
    out.rel_firmaConn.push(...firmen.firmaConn);
    return out;
  }
  // Firma↔Firma-Verbindungen bleiben, sofern es echte Verbindungsarten sind.
  for (const f of toCompanies) {
    out.rel_firmaConn.push({
      id: uid(),
      ref: toRef(f.other),
      relationId: f.relationId,
      relationIds: idsOf([f]),
      srcId: f.srcId,
      destId: f.destId,
      role: firmaConnRole(f, contact?.id),
      share: f.share || undefined,
    });
  }
  return out;
}
